/**
 * 目录编辑页面
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { OutlineData, OutlineItem } from '../types';
import { outlineApi, projectApi, tokenApi } from '../services/api';
import { ChevronRightIcon, ChevronDownIcon, PencilIcon, TrashIcon, PlusIcon, ArrowLeftIcon, ArrowRightIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import TokenAlertDialog from '../components/TokenAlertDialog';
import { useNavigate } from 'react-router-dom';
import OutlineFormatDialog, { OutlineFormat } from '../components/OutlineFormatDialog';
import LoadingOverlay from '../components/LoadingOverlay';
import OutlineGenerateSettingsDialog, { OutlineGenSettings } from '../components/OutlineGenerateSettingsDialog';
import { matchAllChaptersResources, formatResourceSuggestions } from '../utils/resourceMatcher';
import { smartMatchChapters, formatForBatchInit } from '../services/smartMatcher';
import { draftStorage } from '../utils/draftStorage';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ========== 流式目录卡片预览组件 ==========

interface OutlineCardPreviewProps {
  content: string;
}

/**
 * 实时解析流式JSON，提取 title/description 渲染为卡片列表
 */
const OutlineCardPreview: React.FC<OutlineCardPreviewProps> = ({ content }) => {
  // 从流式 JSON 中提取所有 title/description 对
  const extractItems = (text: string): { title: string; description: string; partial?: boolean }[] => {
    const items: { title: string; description: string; partial?: boolean }[] = [];
    
    // 匹配完整的 title + description 对
    const fullPairRegex = /"title"\s*:\s*"((?:[^"\\]|\\.)*)"\s*,\s*"description"\s*:\s*"((?:[^"\\]|\\.)*)"/g;
    let match;
    while ((match = fullPairRegex.exec(text)) !== null) {
      items.push({
        title: match[1].replace(/\\n/g, ' ').replace(/\\"/g, '"'),
        description: match[2].replace(/\\n/g, ' ').replace(/\\"/g, '"'),
      });
    }
    
    // 如果还没有完整对，尝试找到正在流式中的最后一个不完整条目
    if (items.length === 0 || fullPairRegex.lastIndex < text.length) {
      // 找最后出现的 title（可能还没有 description）
      const partialTitleRegex = /"title"\s*:\s*"((?:[^"\\]|\\.)*)"?$/;
      const remaining = text.slice(fullPairRegex.lastIndex);
      const partialMatch = remaining.match(partialTitleRegex);
      if (partialMatch) {
        items.push({
          title: partialMatch[1].replace(/\\n/g, ' ').replace(/\\"/g, '"'),
          description: '',
          partial: true,
        });
      }
    }
    
    return items;
  };

  const items = extractItems(content);

  if (items.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-gray-400 text-sm">
        <span className="animate-spin text-base">⟳</span>
        <span>正在接收数据...</span>
      </div>
    );
  }

  return (
    <div className="p-2">
      {items.map((item, idx) => (
        <div
          key={idx}
          className="flex items-start gap-3 px-3 py-2 rounded-lg mb-1 bg-white/60 border border-gray-100"
        >
          {/* 序号 */}
          <span className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-xs text-blue-500 font-mono">
            {idx + 1}
          </span>
          {/* 内容 */}
          <div className="flex-1 min-w-0 text-left">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-gray-800 leading-snug">
                {item.title}
              </span>
              {item.partial && (
                <span className="text-xs text-gray-400 animate-pulse">▍</span>
              )}
            </div>
            {item.description && (
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed text-left line-clamp-2">
                {item.description}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};


// ========== 工具函数 ==========

/**
 * 递归计算节点及其所有子节点的字数总和
 */
const calculateChildrenWordCount = (item: OutlineItem): number => {
  if (!item.children || item.children.length === 0) {
    // 叶子节点，返回自己的预计字数
    return item.expected_word_count || 0;
  }
  // 父节点，递归计算所有子节点的字数总和
  return item.children.reduce((sum, child) => {
    return sum + calculateChildrenWordCount(child);
  }, 0);
};

// ========== 组件Props接口 ==========

interface OutlineEditProps {
  projectOverview: string;
  techRequirements: string;
  outlineRequirements: string; // 新增
  projectName: string; // 新增：项目名称
  selectedModel: string; // 项目配置的AI模型名称
  outlineData: OutlineData | null;
  onOutlineGenerated: (outline: OutlineData, skipSave?: boolean) => void; // 新增 skipSave 参数
  onNext?: () => void; // 下一步回调
  onPrev?: () => void; // 上一步回调
  nextDisabled?: boolean; // 下一步按钮是否禁用
  projectId?: string; // 新增：项目ID，用于保存资源匹配结果
  projectWordCount?: number; // 新增：项目总字数
  onStepChange?: (step: number) => void; // 新增：步骤变化回调
  tokenBalance?: number | null; // Token余额
}

// 可排序目录项组件的 Props
interface SortableOutlineItemProps {
  item: OutlineItem;
  level?: number;
  expandedItems: Set<string>;
  selectedItem: string | null;
  editingItem: string | null;
  editTitle: string;
  editDescription: string;
  editTitleInputRef: React.RefObject<HTMLInputElement | null>;
  outlineListRef: React.RefObject<HTMLDivElement | null>;
  scrollPositionRef: React.MutableRefObject<number>;
  outlineData: OutlineData | null;
  toggleExpand: (itemId: string) => void;
  setSelectedItem: (id: string) => void;
  addChildItem: (parentId: string) => void;
  setEditingItem: (id: string | null) => void;
  setEditTitle: (title: string) => void;
  setEditDescription: (desc: string) => void;
  removeItemFromOutline: (data: OutlineData, itemId: string) => OutlineData;
  updateItemInOutline: (data: OutlineData, itemId: string, newTitle: string, newDesc: string) => OutlineData;
  onOutlineGenerated: (outline: OutlineData) => void;
  showDeleteConfirm: (item: OutlineItem, hasChildren: boolean) => void;
  showToast: (type: ToastType, title: string, messages?: string[]) => void;
  // 字数编辑相关
  editingWordCount: string | null;
  tempWordCount: string;
  setEditingWordCount: (id: string | null) => void;
  setTempWordCount: (value: string) => void;
  startEditWordCount: (item: OutlineItem) => void;
  saveWordCount: (item: OutlineItem) => void;
  cancelEditWordCount: () => void;
  toggleLock: (item: OutlineItem, dbId?: number) => void;
}

// 将 SortableOutlineItem 提取到组件外部，避免每次渲染时重新创建
const SortableOutlineItem: React.FC<SortableOutlineItemProps> = ({
  item,
  level = 1,
  expandedItems,
  selectedItem,
  editingItem,
  editTitle,
  editDescription,
  editTitleInputRef,
  outlineListRef,
  scrollPositionRef,
  outlineData,
  toggleExpand,
  setSelectedItem,
  addChildItem,
  setEditingItem,
  setEditTitle,
  setEditDescription,
  removeItemFromOutline,
  updateItemInOutline,
  onOutlineGenerated,
  showDeleteConfirm,
  showToast,
  // 字数编辑相关
  editingWordCount,
  tempWordCount,
  setEditingWordCount,
  setTempWordCount,
  startEditWordCount,
  saveWordCount,
  cancelEditWordCount,
  toggleLock,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.has(item.id);
  const isSelected = selectedItem === item.id;
  const isSixthLevel = level >= 6;

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div 
        className={`flex items-center gap-2 p-2 rounded-md group ${
          isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
        }`}
      >
        {/* 拖拽句柄 */}
        <div 
          {...attributes} 
          {...listeners}
          className="flex-shrink-0 cursor-move text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100"
          title="拖动排序"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM11.5 15.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z" />
          </svg>
        </div>

        {/* 展开/收起按钮 */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(item.id);
            }}
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700"
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </button>
        )}
        
        {!hasChildren && <div className="w-5 h-5"></div>}
        
        <div 
          className="flex-1 min-w-0"
          onClick={() => editingItem !== item.id && setSelectedItem(item.id)}
        >
          {editingItem === item.id ? (
            <div
              onBlur={(e) => {
                // 如果焦点转移到当前编辑容器内部，不保存
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                if (outlineData) {
                  const newData = updateItemInOutline(outlineData, item.id, editTitle, editDescription);
                  onOutlineGenerated(newData);
                  setEditingItem(null);
                  showToast('success', '保存成功');
                }
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={editTitleInputRef}
                type="text"
                value={editTitle}
                onChange={(e) => {
                  if (outlineListRef.current) scrollPositionRef.current = outlineListRef.current.scrollTop;
                  setEditTitle(e.target.value);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Escape') setEditingItem(null);
                }}
                className="w-full font-medium text-gray-900 bg-transparent border-b border-gray-200 outline-none focus:outline-none py-0.5"
                style={{ boxShadow: 'none' }}
                placeholder="目录标题"
              />
              <textarea
                value={editDescription}
                rows={2}
                onChange={(e) => {
                  if (outlineListRef.current) scrollPositionRef.current = outlineListRef.current.scrollTop;
                  setEditDescription(e.target.value);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Escape') setEditingItem(null);
                }}
                className="w-full text-sm text-gray-500 bg-transparent border-b border-gray-200 outline-none focus:outline-none py-0.5 mt-1 resize-none"
                style={{ boxShadow: 'none' }}
                placeholder="请认真填写章节描述，描述越详细，AI生成内容越精准"
              />
            </div>
          ) : (
            <>
              <div className="font-medium text-gray-900 truncate">{item.title}</div>
              {item.description && (
                <div className="text-sm text-gray-500 truncate">{item.description}</div>
              )}
            </>
          )}
        </div>

        {/* 字数信息显示 - 竖向对齐，编辑态隐藏 */}
        <div className={`flex items-center gap-2 text-xs flex-shrink-0 ${editingItem === item.id ? 'hidden' : ''}`}>
          {/* 字数信息容器：固定宽度 w-56，内部左右布局 */}
          <div className="flex items-center gap-2 w-56">
            {/* 左侧小盒子：锁定徽章区域（w-20，内容居右） */}
            <div className="w-20 flex justify-end">
              {item.is_locked && (
                <span className="inline-flex items-center text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded text-xs whitespace-nowrap">
                  <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                  已锁定
                </span>
              )}
            </div>

            {/* 右侧小盒子：字数区域（w-32，内容居左） */}
            <div className="w-32">
              {hasChildren ? (
                // 父节点：显示汇总字数（不可编辑，纯文本）
                <div className="flex justify-start items-center text-gray-700 font-medium text-xs py-1">
                  <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="whitespace-nowrap">汇总: {calculateChildrenWordCount(item).toLocaleString()}字</span>
                </div>
              ) : editingWordCount === item.id ? (
                // 叶子节点：编辑模式
                <div 
                  className="flex justify-start"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs">
                    <svg className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-gray-600 font-medium whitespace-nowrap">预计:</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={tempWordCount}
                  onChange={(e) => {
                    // 只允许输入数字
                    const value = e.target.value.replace(/[^\d]/g, '');
                    setTempWordCount(value);
                  }}
                  onBlur={() => {
                    // 失去焦点时保存
                    if (tempWordCount.trim()) {
                      saveWordCount(item);
                    } else {
                      cancelEditWordCount();
                    }
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      saveWordCount(item);
                    } else if (e.key === 'Escape') {
                      cancelEditWordCount();
                    }
                  }}
                  className="w-14 px-1 py-0.5 text-sm text-center bg-gray-50 rounded outline-none focus:bg-white border-0"
                  autoFocus
                  placeholder="0"
                />
                <span className="text-gray-600 font-medium">字</span>
                <button
                  onMouseDown={(e) => {
                    // 使用 onMouseDown 防止 blur 事件先触发
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    saveWordCount(item);
                  }}
                  className="p-0.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                  title="保存"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                  </div>
                </div>
              ) : (
                // 叶子节点：显示模式（可点击编辑，带背景提示）
                <div className="flex justify-start">
                  <span 
                    className="inline-flex items-center text-blue-600 font-medium bg-blue-50 hover:bg-blue-100 cursor-pointer px-2 py-1 rounded transition-all group/wordcount text-xs whitespace-nowrap"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditWordCount(item);
                    }}
                    title="点击编辑字数"
                  >
                    <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="font-medium">预计: {(item.expected_word_count || 0).toLocaleString()}字</span>
                    <svg className="w-3.5 h-3.5 ml-1 opacity-0 group-hover/wordcount:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* 实际字数 */}
          {!!item.actual_word_count && item.actual_word_count > 0 && (
            <span className="inline-flex items-center text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
              <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              实际: {item.actual_word_count.toLocaleString()}字
            </span>
          )}
        </div>

        {/* 操作按钒 */}
        <div className={`flex gap-1 flex-shrink-0 ${editingItem === item.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}>
          {editingItem === item.id ? (
            // 编辑态：显示保存按钒
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (outlineData) {
                  const newData = updateItemInOutline(outlineData, item.id, editTitle, editDescription);
                  onOutlineGenerated(newData);
                  setEditingItem(null);
                  showToast('success', '保存成功');
                }
              }}
              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
              title="保存"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          ) : (
            // 普通态：显示锁定、添加子级、编辑、删除
            <>
              {!hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLock(item, undefined);
                  }}
                  className={`p-1.5 rounded transition-all ${
                    item.is_locked 
                      ? 'text-amber-600 hover:bg-amber-50' 
                      : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                  }`}
                  title={item.is_locked ? '解锁字数' : '锁定字数'}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    {item.is_locked ? (
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                    ) : (
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                    )}
                  </svg>
                </button>
              )}
              {!isSixthLevel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addChildItem(item.id);
                  }}
                  className="p-1 text-gray-400 hover:text-green-600"
                  title="添加子目录"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingItem(item.id);
                  setEditTitle(item.title);
                  setEditDescription(item.description || '');
                  setTimeout(() => editTitleInputRef.current?.focus(), 0);
                }}
                className="p-1 text-gray-400 hover:text-blue-600"
                title="编辑"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  showDeleteConfirm(item, hasChildren || false);
                }}
                className="p-1 text-gray-400 hover:text-red-600"
                title="删除"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* 子项目 - 缩小缩进以适应6级目录 */}
      {hasChildren && isExpanded && (
        <div className="ml-4 pl-1 border-l border-gray-200">
          <SortableContext 
            items={item.children!.map(child => child.id)}
            strategy={verticalListSortingStrategy}
          >
            {item.children!.map(child => (
              <SortableOutlineItem 
                key={`${item.id}-${child.id}`} 
                item={child} 
                level={level + 1}
                expandedItems={expandedItems}
                selectedItem={selectedItem}
                editingItem={editingItem}
                editTitle={editTitle}
                editDescription={editDescription}
                editTitleInputRef={editTitleInputRef}
                outlineListRef={outlineListRef}
                scrollPositionRef={scrollPositionRef}
                outlineData={outlineData}
                toggleExpand={toggleExpand}
                setSelectedItem={setSelectedItem}
                addChildItem={addChildItem}
                setEditingItem={setEditingItem}
                setEditTitle={setEditTitle}
                setEditDescription={setEditDescription}
                removeItemFromOutline={removeItemFromOutline}
                updateItemInOutline={updateItemInOutline}
                onOutlineGenerated={onOutlineGenerated}
                showDeleteConfirm={showDeleteConfirm}
                showToast={showToast}
                editingWordCount={editingWordCount}
                tempWordCount={tempWordCount}
                setEditingWordCount={setEditingWordCount}
                setTempWordCount={setTempWordCount}
                startEditWordCount={startEditWordCount}
                saveWordCount={saveWordCount}
                cancelEditWordCount={cancelEditWordCount}
                toggleLock={toggleLock}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

const OutlineEdit: React.FC<OutlineEditProps> = ({
  projectOverview,
  techRequirements,
  outlineRequirements, // 新增
  projectName, // 新增
  selectedModel, // 项目配置的AI模型名称
  outlineData,
  onOutlineGenerated,
  onNext,
  onPrev,
  nextDisabled = false,
  projectId, // 新增
  projectWordCount = 50000, // 新增：项目总字数，默认50000
  onStepChange, // 新增
  tokenBalance, // Token余额
}) => {
  const navigate = useNavigate();
  
  // Token 欠费弹窗状态
  const [tokenAlert, setTokenAlert] = useState<{
    show: boolean;
    type: 'warning' | 'zero' | 'overdraft';
    title: string;
    message: string;
    canContinue?: boolean;
  }>({ show: false, type: 'zero', title: '', message: '' });
  
  const [generating, setGenerating] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<string | null>(null); // 选中的目录项
  const streamingContainerRef = useRef<HTMLDivElement>(null); // 用于自动滚动到底部
  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({
    show: false,
    type: 'info',
    title: '',
    messages: [],
  });
  const [streamingContent, setStreamingContent] = useState('');
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const editTitleInputRef = useRef<HTMLInputElement>(null); // 编辑标题输入框ref
  const [editingWordCount, setEditingWordCount] = useState<string | null>(null); // 正在编辑字数的章节ID
  const [tempWordCount, setTempWordCount] = useState<string>(''); // 临时字数输入值
  const outlineListRef = useRef<HTMLDivElement>(null); // 目录列表滚动容器ref
  const scrollPositionRef = useRef<number>(0); // 保存滚动位置
  const [matchingResources, setMatchingResources] = useState(false); // 资源匹配中
  const [allocatingWords, setAllocatingWords] = useState(false); // 字数分配中
    const [allocatingWordsOverlay, setAllocatingWordsOverlay] = useState(false); // 自动字数分配中弹窗
  
  // 确认对话框状态
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // 格式选择对话框状态
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<string>('default');  // 默认为'default'
  const [reformatting, setReformatting] = useState(false);

  // 加载已保存的目录格式
  useEffect(() => {
    if (projectId) {
      projectApi.getOutlineFormat(projectId).then((res: any) => {
        const formatId = res?.data?.data?.outline_format_id;
        if (formatId) {
          setCurrentFormat(formatId);
        }
      }).catch(() => {});
    }
  }, [projectId]);

  // 生成目录设置弹框状态
  const [showGenSettings, setShowGenSettings] = useState(false);
  const [genSettings, setGenSettings] = useState<OutlineGenSettings | null>(() => {
    // 从 localStorage 恢复设置
    if (projectId) {
      try {
        const saved = localStorage.getItem(`outline_gen_settings_${projectId}`);
        if (saved) return JSON.parse(saved);
      } catch {}
    }
    return null;
  });

  // 保存设置时同步写入 localStorage
  const handleSaveGenSettings = (settings: OutlineGenSettings) => {
    setGenSettings(settings);
    if (projectId) {
      localStorage.setItem(`outline_gen_settings_${projectId}`, JSON.stringify(settings));
    }
  };

  // 拖拽传感器配置
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 自动滚动到生成内容底部 - 使用 requestAnimationFrame 确保在渲染后滚动
  useEffect(() => {
    if (streamingContent && streamingContainerRef.current) {
      requestAnimationFrame(() => {
        if (streamingContainerRef.current) {
          streamingContainerRef.current.scrollTop = streamingContainerRef.current.scrollHeight;
        }
      });
    }
  }, [streamingContent]);

  // 只在开始编辑时设置一次焦点
  useEffect(() => {
    if (editingItem && editTitleInputRef.current) {
      editTitleInputRef.current.focus();
    }
  }, [editingItem]); // 只在editingItem变化时触发

  // 保存和恢复滚动位置 - 修复：更可靠的方案
  useEffect(() => {
    const container = outlineListRef.current;
    if (!container) return;

    // 保存当前滚动位置
    const currentScrollTop = container.scrollTop;
    
    // 如果不是初始状态（有滚动），保存它
    if (currentScrollTop > 0) {
      scrollPositionRef.current = currentScrollTop;
    }
  });

  // 在编辑内容变化时恢复滚动位置
  useEffect(() => {
    const container = outlineListRef.current;
    if (container && scrollPositionRef.current > 0) {
      // 使用 requestAnimationFrame 确保在 DOM 更新后执行
      requestAnimationFrame(() => {
        container.scrollTop = scrollPositionRef.current;
      });
    }
  }, [editTitle, editDescription]);

  // 显示 Toast
  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  // 关闭 Toast
  const closeToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // 显示删除确认对话框
  const showDeleteConfirm = useCallback((item: OutlineItem, hasChildren: boolean) => {
    setConfirmDialog({
      isOpen: true,
      title: '确认删除',
      message: hasChildren
        ? `确定要删除「${item.title}」吗？\n\n注意：会同时删除该章节下的所有子章节，此操作不可撤销。`
        : `确定要删除「${item.title}」吗？`,
      onConfirm: () => {
        if (outlineData) {
          const newData = removeItemFromOutline(outlineData, item.id);
          onOutlineGenerated(newData);
          if (selectedItem === item.id) {
            setSelectedItem('');
          }
          showToast('success', '删除成功');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  }, [outlineData, selectedItem, onOutlineGenerated]);

  // 处理格式转换 - 直接在前端转换编号格式
  const handleFormatChange = useCallback((format: OutlineFormat) => {
    setFormatDialogOpen(false);
    
    if (!outlineData || !outlineData.outline || outlineData.outline.length === 0) {
      showToast('error', '请先生成目录');
      return;
    }
    
    // 如果是默认格式，移除所有编号，恢复纯标题
    if (format.id === 'default') {
      try {
        const cleanedOutline = removeAllNumbering(outlineData.outline);
        onOutlineGenerated({
          ...outlineData,
          outline: cleanedOutline,
        });
        setCurrentFormat(format.id);
        if (projectId) { projectApi.saveOutlineFormat(projectId, format.id).catch(() => {}); }
        showToast('success', '已恢复为默认格式（无编号）');
      } catch (error: any) {
        console.error('Error removing numbering:', error);
        showToast('error', '格式恢复失败', [error.message]);
      }
      return;
    }
    
    try {
      // 递归转换所有章节的标题编号
      const convertedOutline = convertOutlineFormat(outlineData.outline, format);
      
      // 更新目录数据
      onOutlineGenerated({
        ...outlineData,
        outline: convertedOutline,
      });
      
      setCurrentFormat(format.id);
      if (projectId) { projectApi.saveOutlineFormat(projectId, format.id).catch(() => {}); }
      showToast('success', '目录格式已切换为：' + format.name);
    } catch (error: any) {
      console.error('Error converting format:', error);
      showToast('error', '格式转换失败', [error.message]);
    }
  }, [outlineData, onOutlineGenerated]);
  
  // 移除所有编号，恢复纯标题
  const removeAllNumbering = useCallback((items: OutlineItem[]): OutlineItem[] => {
    return items.map(item => {
      let cleanTitle = item.title;
      
      // 移除所有可能的编号格式
      const patterns = [
        /^第[一二三四五六七八九十百]+章[、，,\s]+/,
        /^第[一二三四五六七八九十百]+节[、，,\s]+/,
        /^第[一二三四五六七八九十百]+条[、，,\s]+/,
        /^[一二三四五六七八九十]+[、，,]/,
        /^（[一二三四五六七八九十]+）[、，,\s]*/,
        /^\d+\.\d+\.\d+\./,
        /^\d+\.\d+\./,
        /^\d+\./,
        /^（\d+）[、，,\s]*/,
      ];
      
      for (const pattern of patterns) {
        cleanTitle = cleanTitle.replace(pattern, '');
      }
      
      cleanTitle = cleanTitle.trim();
      
      const cleanedItem: OutlineItem = {
        ...item,
        title: cleanTitle,
      };
      
      // 递归处理子项
      if (item.children && item.children.length > 0) {
        cleanedItem.children = removeAllNumbering(item.children);
      }
      
      return cleanedItem;
    });
  }, []);
  
  // 转换目录格式的辅助函数
  const convertOutlineFormat = useCallback((items: OutlineItem[], format: OutlineFormat, level: number = 1, parentIndex: number[] = []): OutlineItem[] => {
    return items.map((item, index) => {
      const currentIndex = [...parentIndex, index + 1];
      const newTitle = formatTitle(item.title, level, index + 1, format, currentIndex);
      
      const convertedItem: OutlineItem = {
        ...item,
        title: newTitle,
      };
      
      // 递归处理子项
      if (item.children && item.children.length > 0) {
        convertedItem.children = convertOutlineFormat(item.children, format, level + 1, currentIndex);
      }
      
      return convertedItem;
    });
  }, []);
  
  // 格式化标题：移除旧编号并添加新编号
  const formatTitle = (title: string, level: number, index: number, format: OutlineFormat, fullIndex: number[]): string => {
    // 移除现有的编号（支持多种格式）
    let cleanTitle = title;
    
    // 匹配并移除各种编号格式（包括带前缀的格式）
    const patterns = [
      /^第[一二三四五六七八九十百]+章[、，,\s]+/,  // 第X章、
      /^第[一二三四五六七八九十百]+节[、，,\s]+/,  // 第X节、
      /^第[一二三四五六七八九十百]+条[、，,\s]+/,  // 第X条、
      /^[一二三四五六七八九十]+[、，,]/,            // 一、
      /^（[一二三四五六七八九十]+）[、，,\s]*/,      // （一）
      /^\([一二三四五六七八九十]+\)[、，,\s]*/,    // (一)
      /^[壹贰叁肆伍陆柒捌玖拾]+[、，,\s]*/,          // 壹、
      /^[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳][、，,\s]*/, // ①、
      /^[ⓐⓑⓒⓓⓔⓕⓖⓗⓘⓙⓚⓛⓜⓝⓞⓟⓠⓡⓢⓣ][、，,\s]*/, // ⓐ、
      /^[αβγδεζηθικλμνξοπρστυφχψω][、，,\s]*/,     // α、
      /^[ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ][、，,\s]*/,     // Α、
      /^[IVXivx]+[、，,.\s]*/,                      // I、i、
      /^[A-Z][、，,.\s]*/,                         // A、
      /^[a-z][、，,.\s]*/,                         // a、
      /^（[A-Z]）[、，,\s]*/,                      // （A）
      /^（[a-z]）[、，,\s]*/,                      // （a）
      /^\([A-Z]\)[、，,\s]*/,                     // (A)
      /^\([a-z]\)[、，,\s]*/,                     // (a)
      /^[^\w\s（）()]*[A-Z][、，,.\s]*/,            // 章节A、（前缀+大写字母）
      /^[^\w\s（）()]*[a-z][、，,.\s]*/,            // 章节a、（前缀+小写字母）
      /^\d+\.\d+\.\d+\./,                          // 1.1.1.
      /^\d+\.\d+\./,                                // 1.1.
      /^\d+\./,                                      // 1.
      /^\d+[\s]+/,                                   // 1 （纯数字+空格）
      /^（\d+）[、，,\s]*/,                          // （1）
      /^\(\d+\)[、，,\s]*/,                          // (1)
      /^[^\w\s]*\d+[、，,.\s]*/,                    // 第1项、 或 1、
    ];
    
    for (const pattern of patterns) {
      cleanTitle = cleanTitle.replace(pattern, '');
    }
    
    cleanTitle = cleanTitle.trim();
    
    // 根据格式添加新编号
    const levelFormat = format.levels[level - 1] || format.levels[format.levels.length - 1];
    const prefix = generatePrefix(levelFormat, index, format.id, fullIndex);
    
    return prefix + cleanTitle;
  };
  
  // 根据格式模板生成编号前缀
  const generatePrefix = (formatTemplate: string, index: number, formatId: string, fullIndex: number[]): string => {
    const chineseNumbers = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十',
      '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十',
      '二十一', '二十二', '二十三', '二十四', '二十五', '二十六', '二十七', '二十八', '二十九', '三十'];
    
    const chineseUpperNumbers = ['', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖', '拾',
      '拾壹', '拾贰', '拾叁', '拾肆', '拾伍', '拾陆', '拾柒', '拾捌', '拾玖', '贰拾'];
    
    const romanNumerals = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
      'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'];
    
    const romanLowerNumerals = ['', 'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
      'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx'];
    
    const greekLetters = ['', 'α', 'β', 'γ', 'δ', 'ε', 'ζ', 'η', 'θ', 'ι', 'κ',
      'λ', 'μ', 'ν', 'ξ', 'ο', 'π', 'ρ', 'σ', 'τ', 'υ'];
    
    const greekUpperLetters = ['', 'Α', 'Β', 'Γ', 'Δ', 'Ε', 'Ζ', 'Η', 'Θ', 'Ι', 'Κ',
      'Λ', 'Μ', 'Ν', 'Ξ', 'Ο', 'Π', 'Ρ', 'Σ', 'Τ', 'Υ'];
    
    const circledNumbers = ['', '①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩',
      '⑪', '⑫', '⑬', '⑭', '⑮', '⑯', '⑰', '⑱', '⑲', '⑳'];
    
    const circledLetters = ['', 'ⓐ', 'ⓑ', 'ⓒ', 'ⓓ', 'ⓔ', 'ⓕ', 'ⓖ', 'ⓗ', 'ⓘ', 'ⓙ',
      'ⓚ', 'ⓛ', 'ⓜ', 'ⓝ', 'ⓞ', 'ⓟ', 'ⓠ', 'ⓡ', 'ⓢ', 'ⓣ'];
    
    // 处理带圈数字 ①、②、③
    if (/[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳]/.test(formatTemplate)) {
      return circledNumbers[index] || `${index}`;
    }
    
    // 处理带圈字母 ⓐ、ⓑ、ⓒ
    if (/[ⓐⓑⓒⓓⓔⓕⓖⓗⓘⓙⓚⓛⓜⓝⓞⓟⓠⓡⓢⓣ]/.test(formatTemplate)) {
      return circledLetters[index] || `${index}`;
    }
    
    // 处理大写希腊字母 Α、Β、Γ
    if (/[ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ]/.test(formatTemplate)) {
      return greekUpperLetters[index] || `${index}`;
    }
    
    // 处理小写希腊字母 α、β、γ
    if (/[αβγδεζηθικλμνξοπρστυφχψω]/.test(formatTemplate)) {
      return greekLetters[index] || `${index}`;
    }
    
    // 处理大写罗马数字 I、II、III
    if (/^[IVX]+[、，,.\s]*/.test(formatTemplate)) {
      return romanNumerals[index] || `${index}`;
    }
    
    // 处理小写罗马数字 i、ii、iii
    if (/^[ivx]+[、，,.\s]*/.test(formatTemplate)) {
      return romanLowerNumerals[index] || `${index}`;
    }
    
    // 处理中文大写数字 壹、贰、叁
    if (/[壹贰叁肆伍陆柒捌玖拾]/.test(formatTemplate)) {
      return chineseUpperNumbers[index] || `${index}`;
    }
    
    // 处理包含大写字母的格式（支持前缀和后缀）
    const upperLetterMatch = formatTemplate.match(/([^A-Z]*)([A-Z])(.*)/);
    if (upperLetterMatch && /[A-Z]/.test(formatTemplate)) {
      const [, prefix, , suffix] = upperLetterMatch;
      const letter = String.fromCharCode(64 + index);
      return prefix + letter + suffix;
    }
    
    // 处理包含小写字母的格式（支持前缀和后缀）
    const lowerLetterMatch = formatTemplate.match(/([^a-z]*)([a-z])(.*)/);
    if (lowerLetterMatch && /[a-z]/.test(formatTemplate)) {
      const [, prefix, , suffix] = lowerLetterMatch;
      const letter = String.fromCharCode(96 + index);
      return prefix + letter + suffix;
    }
    
    // 第X章、第X节、第X条
    if (formatTemplate.includes('第') && formatTemplate.includes('章')) {
      return `第${chineseNumbers[index]}章、`;
    }
    if (formatTemplate.includes('第') && formatTemplate.includes('节')) {
      return `第${chineseNumbers[index]}节、`;
    }
    if (formatTemplate.includes('第') && formatTemplate.includes('条')) {
      return `第${chineseNumbers[index]}条、`;
    }
    
    // 一、二、三、
    if (formatTemplate.match(/^[一二三四五六七八九十]+[、，,]/)) {
      return `${chineseNumbers[index]}、`;
    }
    
    // （一）（二）（三）
    if (formatTemplate.match(/^（[一二三四五六七八九十]+）/)) {
      return `（${chineseNumbers[index]}）`;
    }
    
    // (一)(二)(三) 英文括号
    if (formatTemplate.match(/^\([一二三四五六七八九十]+\)/)) {
      return `(${chineseNumbers[index]})`;
    }
    
    // （1）（2）（3）
    if (formatTemplate.match(/^（\d+）/)) {
      return `（${index}）`;
    }
    
    // (1)(2)(3) 英文括号
    if (formatTemplate.match(/^\(\d+\)/)) {
      return `(${index})`;
    }
    
    // 层级编号格式：1.1.1.
    if (formatTemplate.match(/^\d+\.\d+\.\d+\./)) {
      return fullIndex.join('.') + '. ';
    }
    
    // 层级编号格式：1.1.
    if (formatTemplate.match(/^\d+\.\d+\./)) {
      if (fullIndex.length >= 2) {
        return fullIndex.slice(0, 2).join('.') + '. ';
      }
      return `${index}.${index}. `;
    }
    
    // 1. 2. 3.
    if (formatTemplate.match(/^\d+\./)) {
      return `${index}. `;
    }
    
    // 处理包含数字的格式（支持前缀和后缀）
    const numberMatch = formatTemplate.match(/([^\d]*)(\d+)(.*)/);
    if (numberMatch) {
      const [, prefix, , suffix] = numberMatch;
      return prefix + index + suffix;
    }
    
    // 默认返回数字编号
    return `${index}. `;
  };



  // 智能分配字数
  const handleAllocateWords = async (customOutlineData?: OutlineData) => {
    // 优先使用传入的目录数据，否则使用props的outlineData
    const targetOutlineData = customOutlineData || outlineData;
    
    if (!targetOutlineData || !projectId) {
      showToast('error', '无法分配字数', ['缺少目录数据或项目ID']);
      return;
    }

    try {
      setAllocatingWords(true);
      
      const response = await outlineApi.allocateWords(projectId, targetOutlineData.outline, true);
      
      if (response.data.success) {
        // 更新目录数据（注意：后端已保存，无需再次保存）
        const newData = {
          ...targetOutlineData,
          outline: response.data.outline
        };
        // 直接更新本地状态，跳过保存逻辑
        onOutlineGenerated(newData, true); // 传递 skipSave 标志
        
        const stats = response.data.stats;
        showToast('success', '字数分配成功', [
          `已分配预计字数：${stats.expected_total || 0} 字`,
          `已锁定字数：${stats.locked_total || 0} 字`,
          `项目总字数：${response.data.total_word_count || 0} 字`
        ]);
      }
    } catch (error: any) {
      console.error('字数分配失败:', error);
      showToast('error', '字数分配失败', [
        error.response?.data?.detail || error.message || '未知错误'
      ]);
    } finally {
      setAllocatingWords(false);
    }
  };

  // 计算总字数统计
  const calculateTotalStats = () => {
    if (!outlineData || !outlineData.outline) {
      return { expected: 0, actual: 0, locked: 0 };
    }

    const calculate = (items: OutlineItem[]) => {
      let expected = 0;
      let actual = 0;
      let locked = 0;

      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          // 递归计算子节点
          const childStats = calculate(item.children);
          expected += childStats.expected;
          actual += childStats.actual;
          locked += childStats.locked;
        } else {
          // 叶子节点
          expected += item.expected_word_count || 0;
          actual += item.actual_word_count || 0;
          if (item.is_locked && item.expected_word_count) {
            locked += item.expected_word_count;
          }
        }
      });

      return { expected, actual, locked };
    };

    return calculate(outlineData.outline);
  };

  // 开始编辑字数
  const startEditWordCount = (item: OutlineItem) => {
    setEditingWordCount(item.id);
    setTempWordCount(String(item.expected_word_count || ''));
  };

  // 保存字数修改
  const saveWordCount = async (item: OutlineItem) => {
    const newCount = parseInt(tempWordCount);
    if (isNaN(newCount) || newCount < 0) {
      showToast('error', '无效字数', ['请输入有效的数字']);
      setEditingWordCount(null);
      return;
    }
  
    if (!projectId) {
      showToast('error', '保存失败', ['缺少项目ID']);
      return;
    }
  
    try {
      // 调用批量更新接口（只更新一个项）
      await outlineApi.updateWordCount(projectId, [{
        item_id: item.id,
        expected_word_count: newCount
      }]);
  
      // 更新本地状态
      const updateWordCount = (items: OutlineItem[]): OutlineItem[] => {
        return items.map(i => {
          if (i.id === item.id) {
            return { ...i, expected_word_count: newCount };
          }
          if (i.children) {
            return { ...i, children: updateWordCount(i.children) };
          }
          return i;
        });
      };
  
      const newData = {
        ...outlineData!,
        outline: updateWordCount(outlineData!.outline)
      };
        
      // 直接更新本地状态，不触发保存（后端已保存）
      onOutlineGenerated(newData, true);
      setEditingWordCount(null);
        
      showToast('success', '字数已更新', [
        `章节“${item.title}”的预计字数已更新为 ${newCount} 字`
      ]);
    } catch (error: any) {
      console.error('字数更新失败:', error);
      showToast('error', '字数更新失败', [
        error.response?.data?.detail || error.message || '未知错误'
      ]);
    }
  };

  // 取消编辑字数
  const cancelEditWordCount = () => {
    setEditingWordCount(null);
    setTempWordCount('');
  };

  // 切换锁定状态
  const toggleLock = async (item: OutlineItem, dbId?: number) => {
    // 暂时只更新本地状态，不调用API（因为需要数据库ID）
    const newLockState = !item.is_locked;
    
    // 更新本地状态
    const updateLock = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(i => {
        if (i.id === item.id) {
          return { ...i, is_locked: newLockState };
        }
        if (i.children) {
          return { ...i, children: updateLock(i.children) };
        }
        return i;
      });
    };
    
    const newData = {
      ...outlineData!,
      outline: updateLock(outlineData!.outline)
    };
    // skipSave=true：锁定状态变更由 allocateWords 接口负责持久化，避免整棵目录树重写
    onOutlineGenerated(newData, true);
    
    showToast('success', newLockState ? '已锁定' : '已解锁', [
      `章节"${item.title}"的字数已${newLockState ? '锁定' : '解锁'}`
    ]);
  };

  // 处理下一步 - 在跳转前进行智能资源匹配
  const handleNextClick = async () => {
    if (!onNext || !outlineData || !projectId) {
      onNext?.();
      return;
    }

    try {
      setMatchingResources(true);
      
      // 使用新的动态分词匹配算法（无固定关键词）
      const matchResults = await smartMatchChapters(projectId, outlineData.outline);
      
      // 格式化为batch-init所需格式
      const formattedSuggestions = formatForBatchInit(matchResults);
      
      // 调用后端API批量初始化章节资源
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/batch-init`,
        { suggestions: formattedSuggestions }
      );
      
      // 调用原始的onNext
      onNext();
      
    } catch (error) {
      console.error('❌ 资源匹配失败:', error);
      showToast('error', '资源匹配失败，但仍将继续');
      // 失败也继续下一步
      onNext();
    } finally {
      setMatchingResources(false);
    }
  };

  // 处理生成目录
  const handleGenerateOutline = async () => {
    if (!projectOverview.trim() || !techRequirements.trim()) {
      showToast('error', '请先完成招标解析，获取项目概述和技术评分要求');
      return;
    }

    // 检查 Token 余额：如果已欠费（小于0），显示欠费弹窗，但允许继续生成
    if (tokenBalance !== undefined && tokenBalance !== null && tokenBalance < 0) {
      setTokenAlert({
        show: true,
        type: 'zero',
        title: 'Token 已欠费',
        message: `您的 Token 已欠费 ${Math.abs(tokenBalance).toLocaleString()}，欠费超过 10,000 后将暂停 AI 生成。是否继续生成？`,
        canContinue: true
      });
      return;
    }

    // 如果已有目录数据，显示确认对话框
    if (outlineData && outlineData.outline && outlineData.outline.length > 0) {
      setConfirmDialog({
        isOpen: true,
        title: '确认重新生成',
        message: '重新生成将覆盖当前目录结构，已生成的章节内容将被清空。\n\n确定重新生成吗？',
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          doGenerateOutline();
        }
      });
    } else {
      // 没有现有目录，直接生成
      doGenerateOutline();
    }
  };
  
  // 欠费时继续生成目录
  const handleContinueGenerate = () => {
    setTokenAlert(prev => ({ ...prev, show: false }));
    // 如果已有目录数据，显示确认对话框
    if (outlineData && outlineData.outline && outlineData.outline.length > 0) {
      setConfirmDialog({
        isOpen: true,
        title: '确认重新生成',
        message: '重新生成将覆盖当前目录结构，已生成的章节内容将被清空。\n\n确定重新生成吗？',
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          doGenerateOutline(true);
        }
      });
    } else {
      // 没有现有目录，直接生成（强制透支）
      doGenerateOutline(true);
    }
  };
  
  // 执行生成目录
  const doGenerateOutline = async (forceOverdraft: boolean = false) => {
    // 清空本地缓存的章节内容
    draftStorage.clearAllContent();
    
    // 重置项目进度到目录生成完成阶段（步骤3），并通知父组件更新步骤显示
    if (projectId) {
      try {
        await projectApi.updateProgress(projectId, { max_reached_step: 3 });
        // 通知父组件步骤已变化，更新左侧步骤栏
        onStepChange?.(3);
      } catch (e) {
        console.error('重置项目进度失败:', e);
      }
    }
    
    // 只更新本地状态以显示生成动画，不保存到DB（避免生成成功前就把旧目录清空）
    onOutlineGenerated({ outline: [] }, true);
    setGenerating(true);
    setStreamingContent('');
    
    // 用于存储生成的目录数据，以便自动分配字数
    let generatedOutlineData: OutlineData | null = null;

    try {
      // 构建请求数据
      const requestData = {
        overview: projectOverview,
        requirements: techRequirements,
        outline_requirements: outlineRequirements, // 新增
        project_name: projectName, // 新增：传递项目名称
        old_outline: outlineData ? JSON.stringify(outlineData) : undefined,
        selected_model: selectedModel, // 项目配置的AI模型名称
        project_id: projectId, // 项目ID（用于Token使用记录）
        // 自定义提示词设置
        outline_requirements_custom_prompt: genSettings?.outlineRequirementsPrompt || undefined,
      };

      // 发起流式生成请求
      const response = await outlineApi.generateOutlineStream(requestData, forceOverdraft);
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('无法读取响应流');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullJsonContent = ''; // 用于积累完整的JSON内容

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // 将字节转换为文本并添加到缓冲区
          buffer += decoder.decode(value, { stream: true });
          
          // 按行分割处理SSE数据
          const lines = buffer.split('\n');
          // 保留最后一个可能不完整的行
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            const trimmedLine = line.trim();
            
            // 检查是否是结束信号
            if (trimmedLine === 'data: [DONE]') {
              // 流式传输结束，尝试解析积累的JSON内容
              if (fullJsonContent.trim()) {
                try {
                  // 尝试清理并解析JSON（处理末尾多余字符）
                  let jsonStr = fullJsonContent.trim();
                  // 找到最外层完整的 { } 包裹
                  const firstBrace = jsonStr.indexOf('{');
                  const lastBrace = jsonStr.lastIndexOf('}');
                  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
                    jsonStr = jsonStr.slice(firstBrace, lastBrace + 1);
                  }
                  const outlineData = JSON.parse(jsonStr);
                  // 添加项目名称和项目概述到目录数据中（使用props传入的权威数据）
                  const outlineDataWithName = {
                    ...outlineData,
                    project_name: projectName,
                    project_overview: projectOverview  // 强制使用props中的项目概述，不使用AI生成的
                  };
                  onOutlineGenerated(outlineDataWithName, true); // skipSave=true，由下方 saveOutline 负责落库
                  // 重置为默认格式
                  setCurrentFormat('default');
                  
                  // 直接await保存到DB，确保 allocateWords 之前数据已落库
                  if (projectId) {
                    await projectApi.saveOutline(projectId, outlineDataWithName);
                  }
                  
                  // 保存目录数据以便后续自动分配字数
                  generatedOutlineData = outlineDataWithName;
                } catch (e) {
                  console.error('Failed to parse complete JSON:', e);
                  throw new Error('目录数据解析失败');
                }
              }
              continue;
            }
            
            // 解析 "data: {json}" 格式
            if (trimmedLine.startsWith('data: ')) {
              try {
                const jsonStr = trimmedLine.substring(6); // 移除 "data: " 前缀
                const data = JSON.parse(jsonStr);
                
                // 处理流式内容
                if (data.chunk) {
                  setStreamingContent(prev => prev + data.chunk);
                  fullJsonContent += data.chunk; // 积累完整JSON
                }
                
                // 处理错误消息
                if (data.error) {
                  throw new Error(data.message || '目录生成失败');
                }
              } catch (e) {
                // 如果不是JSON格式，忽略
                if (e instanceof SyntaxError) {
                  console.warn('Failed to parse SSE data:', trimmedLine);
                } else {
                  throw e;
                }
              }
            }
          }
        }
        
        // 处理缓冲区中剩余的数据
        if (buffer.trim()) {
          const trimmedLine = buffer.trim();
          
          if (trimmedLine === 'data: [DONE]') {
            if (fullJsonContent.trim()) {
              try {
                let jsonStr = fullJsonContent.trim();
                const firstBrace = jsonStr.indexOf('{');
                const lastBrace = jsonStr.lastIndexOf('}');
                if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
                  jsonStr = jsonStr.slice(firstBrace, lastBrace + 1);
                }
                const outlineData = JSON.parse(jsonStr);
                // 添加项目名称和项目概述到目录数据中（使用props传入的权威数据）
                const outlineDataWithName = {
                  ...outlineData,
                  project_name: projectName,
                  project_overview: projectOverview  // 强制使用props中的项目概述，不使用AI生成的
                };
                onOutlineGenerated(outlineDataWithName, true); // skipSave=true，由下方 saveOutline 负责落库
                // 重置为默认格式
                setCurrentFormat('default');
                
                // 直接await保存到DB，确保 allocateWords 之前数据已落库
                if (projectId) {
                  await projectApi.saveOutline(projectId, outlineDataWithName);
                }
                
                // 保存目录数据以便后续自动分配字数
                generatedOutlineData = outlineDataWithName;
              } catch (e) {
                console.error('Failed to parse complete JSON:', e);
                throw new Error('目录数据解析失败');
              }
            }
          } else if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.substring(6);
              const data = JSON.parse(jsonStr);
              if (data.chunk) {
                setStreamingContent(prev => prev + data.chunk);
                fullJsonContent += data.chunk;
              }
              if (data.error) {
                throw new Error(data.message || '目录生成失败');
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                throw e;
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      showToast('success', '目录生成完成！');
      
      // 触发Token刷新事件
      window.dispatchEvent(new CustomEvent('token:used'));
      console.log('✨ 目录生成完成');
      
      // 立即关闭生成动画，目录马上可见
      setGenerating(false);
      
      // 自动分配字数：显示加载框，等待3秒后执行
      if (generatedOutlineData) {
        setAllocatingWordsOverlay(true);
        try {
          await new Promise(resolve => setTimeout(resolve, 3000));
          await handleAllocateWords(generatedOutlineData);
        } finally {
          setAllocatingWordsOverlay(false);
        }
      }

    } catch (error) {
      console.error('目录生成失败:', error);
      const errorMsg = (error as Error)?.message || '';
      // Token 欠费错误不显示 Toast，会自动弹出欠费弹框
      if (!errorMsg.includes('Token') && !errorMsg.includes('欠费') && !errorMsg.includes('余额')) {
        showToast('error', '目录生成失败，请重试');
      }
    } finally {
      setGenerating(false);
    }
  };

  // 切换项目展开状态
  const toggleExpand = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // 全部展开
  const expandAll = useCallback(() => {
    if (!outlineData) return;
    const allIds = new Set<string>();
    const collectIds = (items: OutlineItem[]) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          allIds.add(item.id);
          collectIds(item.children);
        }
      });
    };
    collectIds(outlineData.outline);
    setExpandedItems(allIds);
  }, [outlineData]);

  // 全部收缩
  const collapseAll = useCallback(() => {
    setExpandedItems(new Set());
  }, []);

  // 从目录中删除项目
  const removeItemFromOutline = (outlineData: OutlineData, itemId: string): OutlineData => {
    const removeItem = (items: OutlineItem[]): OutlineItem[] => {
      return items
        .map(item => ({ ...item }))
        .filter(item => item.id !== itemId)
        .map(item => {
          if (item.children) {
            return { ...item, children: removeItem(item.children) };
          }
          return item;
        });
    };

    return {
      ...outlineData,
      outline: removeItem(outlineData.outline)
    };
  };

  // 更新目录中的项目
  const updateItemInOutline = (outlineData: OutlineData, itemId: string, title: string, description: string): OutlineData => {
    const updateItem = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          return { ...item, title, description };
        }
        if (item.children) {
          return { ...item, children: updateItem(item.children) };
        }
        return item;
      });
    };

    return {
      ...outlineData,
      outline: updateItem(outlineData.outline)
    };
  };

  // 添加根目录项（优化：支持选中后添加同级）
  const addRootItem = () => {
    if (!outlineData) return;

    const newItem: OutlineItem = {
      id: `item_${Date.now()}`,
      title: '新目录项',
      description: '',
      children: []
    };

    let newOutline: OutlineItem[];

    if (selectedItem) {
      // 如果有选中项，在同级下方插入
      newOutline = insertSiblingAfter(outlineData.outline, selectedItem, newItem);
    } else {
      // 否则添加到根级最后
      newOutline = [...outlineData.outline, newItem];
    }

    const newData = {
      ...outlineData,
      outline: newOutline
    };
    onOutlineGenerated(newData);
    setSelectedItem(newItem.id);
    setEditingItem(newItem.id);
    setEditTitle(newItem.title);
    setEditDescription(newItem.description);
  };

  // 在指定项后插入同级项
  const insertSiblingAfter = (items: OutlineItem[], targetId: string, newItem: OutlineItem): OutlineItem[] => {
    const result: OutlineItem[] = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      result.push(item);
      
      // 找到目标项，在其后插入
      if (item.id === targetId) {
        result.push(newItem);
      }
      
      // 递归处理子项
      if (item.children && item.children.length > 0) {
        const updatedChildren = insertSiblingAfter(item.children, targetId, newItem);
        // 如果子项有变化，更新
        if (updatedChildren.length !== item.children.length) {
          result[result.length - 1] = {
            ...item,
            children: updatedChildren
          };
        }
      }
    }
    
    return result;
  };

  // 添加子目录（最多六级）
  const addChildItem = (parentId: string) => {
    if (!outlineData) return;

    // 检查父项级别
    const getItemLevel = (items: OutlineItem[], itemId: string, level: number = 1): number | null => {
      for (const item of items) {
        if (item.id === itemId) {
          return level;
        }
        if (item.children && item.children.length > 0) {
          const childLevel = getItemLevel(item.children, itemId, level + 1);
          if (childLevel !== null) return childLevel;
        }
      }
      return null;
    };

    const parentLevel = getItemLevel(outlineData.outline, parentId);
    if (parentLevel !== null && parentLevel >= 6) {
      showToast('error', '最多支持六级目录');
      return;
    }

    const newItem: OutlineItem = {
      id: `item_${Date.now()}`,
      title: '新子目录',
      description: '',
      children: []
    };

    const addChild = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(item => {
        if (item.id === parentId) {
          return {
            ...item,
            children: [...(item.children || []), newItem]
          };
        }
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: addChild(item.children)
          };
        }
        return item;
      });
    };

    const newData = {
      ...outlineData,
      outline: addChild(outlineData.outline)
    };
    onOutlineGenerated(newData);
    // 自动展开父项
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      newSet.add(parentId);
      return newSet;
    });
    setSelectedItem(newItem.id);
    setEditingItem(newItem.id);
    setEditTitle(newItem.title);
    setEditDescription(newItem.description);
  };

  // 处理拖拽结束 - 修复：支持嵌套级别拖拽
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !outlineData) {
      return;
    }

    // 递归查找并重排序
    const reorderItems = (items: OutlineItem[]): { items: OutlineItem[]; found: boolean } => {
      const ids = items.map(item => item.id);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);

      // 如果在当前级别找到了
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        return { items: newItems, found: true };
      }

      // 递归处理子项
      let found = false;
      const newItems = items.map(item => {
        if (item.children && item.children.length > 0) {
          const result = reorderItems(item.children);
          if (result.found) {
            found = true;
            return { ...item, children: result.items };
          }
        }
        return item;
      });

      return { items: newItems, found };
    };

    const result = reorderItems(outlineData.outline);
    if (result.found) {
      onOutlineGenerated({
        ...outlineData,
        outline: result.items
      });
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
        duration={5000}
      />

      {/* 操作按钮 */}
      <div className="bg-white rounded-lg shadow p-6 flex-shrink-0 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-gray-900">📋 目录生成</h2>
          
          {/* 按钮组 - 水平对齐，放在最右侧 */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handleGenerateOutline}
              disabled={generating || !projectOverview || !techRequirements}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
            >
              {generating ? (
                <>
                  <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  正在生成目录...
                </>
              ) : (
                '生成目录结构'
              )}
            </button>
            <button
              onClick={() => setShowGenSettings(true)}
              disabled={generating}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="生成目录设置"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 描述文字 */}
        <p className="text-sm text-gray-500 mb-4">
          基于第二步:【招标解析】中的目录要求信息进行生成，标书将按此目录进行生成，请认真修改。
        </p>

        {!projectOverview && !techRequirements && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              请先在“标书解析”步骤中完成文档分析，获取项目概述和技术评分要求。
            </p>
          </div>
        )}
      </div>
      
      {/* 空状态占位提示 / 生成过程展示 - 还没有生成目录或正在生成时显示 */}
      {(() => {
        const shouldShowEmpty = !outlineData || !outlineData.outline || outlineData.outline.length === 0;
        const shouldShowGenerating = generating || reformatting;
        return shouldShowEmpty || shouldShowGenerating;
      })() && (
        <div className="bg-white rounded-lg shadow p-6 flex-1 flex flex-col items-center justify-center min-h-0 mb-4">
          {(generating || reformatting) ? (
            /* 生成过程中 - 科技感动画 */
            <div className="text-center max-w-2xl space-y-6 relative z-10 w-full">
              {/* 中心动画区域 - 只有旋转光环 */}
              <div className="relative">
                {/* 多层旋转光环 */}
                <div className="relative w-32 h-32 mx-auto">
                  {/* 外层光环 */}
                  <div className="absolute inset-0 rounded-full border-4 border-blue-200 border-t-blue-500 animate-spin" style={{ animationDuration: '3s' }}></div>
                  {/* 中层光环 */}
                  <div className="absolute inset-3 rounded-full border-4 border-purple-200 border-r-purple-500 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}></div>
                  {/* 内层光环 */}
                  <div className="absolute inset-6 rounded-full border-4 border-indigo-200 border-b-indigo-500 animate-spin" style={{ animationDuration: '1.5s' }}></div>
                            
                  {/* 光晕效果 */}
                  <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 blur-xl animate-pulse"></div>
                </div>
              </div>
                        
              {/* 文字区域 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 animate-pulse">
                  {reformatting ? 'AI 章节更新中' : 'AI 智能生成中'}
                </h3>
                <p className="text-sm text-gray-600">
                  {reformatting ? '正在更新目录结构...' : '分析招标文件，构建目录框架...'}
                </p>
              </div>
                        
              {/* 加载动画 */}
              <div className="flex justify-center space-x-2">
                <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
                        
              {/* 流式内容显示区 - 科技感JSON预览 */}
              {streamingContent && (
                <div className="mt-4 w-full max-w-2xl">
                  <div 
                    className="bg-slate-900 rounded-lg overflow-hidden shadow-2xl border border-slate-700"
                  >
                    {/* 标题栏 */}
                    <div className="bg-slate-800 px-4 py-2 flex items-center justify-between border-b border-slate-700">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <span className="ml-3 text-xs text-slate-400 font-mono">outline.json</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">实时生成中</span>
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                      </div>
                    </div>
                    
                    {/* 卡片列表区 */}
                    <div 
                      ref={streamingContainerRef}
                      className="overflow-y-scroll bg-gray-50"
                      style={{
                        height: '300px',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      } as React.CSSProperties}
                    >
                      <OutlineCardPreview content={streamingContent} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* 空状态 - 等待生成 */
            <div className="text-center max-w-md space-y-6">
              {/* 图标区域 */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-blue-100 rounded-full blur-2xl opacity-50"></div>
                  <div className="relative text-8xl animate-pulse">📝</div>
                </div>
              </div>
                    
              {/* 文字提示 */}
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-gray-800">
                  还没有生成目录结构
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  点击上方的 <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">🤖 生成目录结构</span> 按钮<br />
                  AI 将基于招标解析中的目录要求自动生成标书目录结构
                </p>
              </div>
                    
              {/* 功能亮点 */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="flex flex-col items-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <div className="text-3xl mb-2">✨</div>
                  <div className="text-xs font-medium text-gray-700">AI 智能生成</div>
                  <div className="text-xs text-gray-500 mt-1">符合招标要求</div>
                </div>
                      
                <div className="flex flex-col items-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                  <div className="text-3xl mb-2">✏️</div>
                  <div className="text-xs font-medium text-gray-700">自由编辑</div>
                  <div className="text-xs text-gray-500 mt-1">可增删改查</div>
                </div>
                      
                <div className="flex flex-col items-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100">
                  <div className="text-3xl mb-2">🔄</div>
                  <div className="text-xs font-medium text-gray-700">拖拽排序</div>
                  <div className="text-xs text-gray-500 mt-1">灵活调整顺序</div>
                </div>
                      
                <div className="flex flex-col items-center p-4 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg border border-orange-100">
                  <div className="text-3xl mb-2">🎯</div>
                  <div className="text-xs font-medium text-gray-700">多种格式</div>
                  <div className="text-xs text-gray-500 mt-1">支持多种编号</div>
                </div>
              </div>
                    
              {/* 底部提示 */}
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-400 mt-6">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>生成后可随时修改和调整目录结构</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 目录预览 - 生成过程中隐藏 */}
      {outlineData && outlineData.outline && outlineData.outline.length > 0 && !generating && !reformatting && (
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          {/* 总字数统计区域 */}
          {(() => {
            const stats = calculateTotalStats();
            const projectTotal = projectWordCount; // 使用传入的项目总字数
            const remaining = projectTotal - stats.expected;
            const percentage = projectTotal > 0 ? ((stats.expected / projectTotal * 100).toFixed(1)) : '0';
            
            return (
              <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-800 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    字数统计总览
                  </h4>
                  <span className={`text-xs px-2 py-1 rounded ${
                    Math.abs(remaining) / projectTotal > 0.3 
                      ? 'bg-red-100 text-red-700' 
                      : remaining < 0 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                  }`}>
                    {remaining >= 0 ? '正常' : '超支'}
                  </span>
                </div>
                
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">项目总字数</div>
                    <div className="text-lg font-bold text-gray-900">{projectTotal.toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-0.5">目标</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">已分配字数</div>
                    <div className="text-lg font-bold text-blue-600">{stats.expected.toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{percentage}% 已分配</div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">剩余可分配</div>
                    <div className={`text-lg font-bold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {remaining >= 0 ? remaining.toLocaleString() : `超${Math.abs(remaining).toLocaleString()}`}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {remaining >= 0 ? '可用' : '已超'}
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">已锁定字数</div>
                    <div className="text-lg font-bold text-amber-600">{stats.locked.toLocaleString()}</div>
                    <div className="text-xs text-gray-400 mt-0.5">受保护</div>
                  </div>
                </div>
                
                {/* 进度条 */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>分配进度</span>
                    <span className={
                      parseFloat(percentage) > 120 ? 'text-red-600 font-semibold' :
                      parseFloat(percentage) > 100 ? 'text-orange-600 font-semibold' :
                      parseFloat(percentage) >= 95 ? 'text-green-600 font-semibold' :
                      'text-gray-600'
                    }>{percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        parseFloat(percentage) > 120 
                          ? 'bg-gradient-to-r from-red-600 to-red-700' 
                          : parseFloat(percentage) > 100
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600' 
                          : parseFloat(percentage) >= 95
                          ? 'bg-gradient-to-r from-green-500 to-green-600'
                          : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                      }`}
                      style={{ 
                        width: `${Math.min(parseFloat(percentage), 100)}%`  // 进度条最大100%，不超出容器
                      }}
                    />
                  </div>
                  {/* 进度条说明 */}
                  <div className="mt-1 text-xs text-gray-500">
                    {parseFloat(percentage) > 120 ? '⚠️ 分配过多，建议调整' :
                     parseFloat(percentage) > 100 ? '✓ 适度超出，符合预期' :
                     parseFloat(percentage) >= 95 ? '✓ 接近目标，正常范围' :
                     parseFloat(percentage) >= 80 ? '可继续分配' :
                     '字数分配偏少'}
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* 面板头部 - sticky 固定 */}
          <div className="flex items-center justify-between mb-4 sticky -top-6 bg-white z-10 py-2 border-b">
            <h3 className="text-lg font-medium text-gray-900">目录预览</h3>
            
            {/* 按钮组 */}
            <div className="flex items-center space-x-2">
              {/* 添加目录按钮 */}
              <button
                onClick={addRootItem}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                title={selectedItem ? '在选中项下方添加同级' : '添加根目录'}
              >
                <PlusIcon className="h-3.5 w-3.5 mr-1" />
                添加目录
              </button>

              {/* 目录格式按钮 */}
              <button
                onClick={() => setFormatDialogOpen(true)}
                disabled={reformatting || generating}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="选择目录编号格式"
              >
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                目录格式
              </button>

              {/* 分配字数按钮 */}
              <button
                onClick={() => handleAllocateWords()}
                disabled={allocatingWords || reformatting || generating}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="根据项目总字数智能分配各章节字数"
              >
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {allocatingWords ? '分配中...' : '分配字数'}
              </button>
              
              {/* 展开按钮 */}
              <button
                onClick={expandAll}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                title="展开全部"
              >
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
                展开
              </button>
              
              {/* 收缩按钮 */}
              <button
                onClick={collapseAll}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                title="收缩全部"
              >
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                </svg>
                收缩
              </button>
            </div>
          </div>
          
          {/* 可拖拽排序的目录列表 - 放开滚动，页面自然滚动 */}
          <div 
            ref={outlineListRef}
            className="border rounded-lg p-4"
          >
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={outlineData.outline.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {outlineData.outline.map((item, index) => (
                  <SortableOutlineItem 
                    key={`outline-${index}-${item.id}`} 
                    item={item} 
                    level={1}
                    expandedItems={expandedItems}
                    selectedItem={selectedItem}
                    editingItem={editingItem}
                    editTitle={editTitle}
                    editDescription={editDescription}
                    editTitleInputRef={editTitleInputRef}
                    outlineListRef={outlineListRef}
                    scrollPositionRef={scrollPositionRef}
                    outlineData={outlineData}
                    toggleExpand={toggleExpand}
                    setSelectedItem={setSelectedItem}
                    addChildItem={addChildItem}
                    setEditingItem={setEditingItem}
                    setEditTitle={setEditTitle}
                    setEditDescription={setEditDescription}
                    removeItemFromOutline={removeItemFromOutline}
                    updateItemInOutline={updateItemInOutline}
                    onOutlineGenerated={onOutlineGenerated}
                    showDeleteConfirm={showDeleteConfirm}
                    showToast={showToast}
                    editingWordCount={editingWordCount}
                    tempWordCount={tempWordCount}
                    setEditingWordCount={setEditingWordCount}
                    setTempWordCount={setTempWordCount}
                    startEditWordCount={startEditWordCount}
                    saveWordCount={saveWordCount}
                    cancelEditWordCount={cancelEditWordCount}
                    toggleLock={toggleLock}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}

      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* 自动字数分配中弹窗 */}
      <LoadingOverlay show={allocatingWordsOverlay} message="自动分配字数中..." />

      {/* 目录格式选择对话框 */}
      <OutlineFormatDialog
        isOpen={formatDialogOpen}
        currentFormat={currentFormat}
        onConfirm={handleFormatChange}
        onCancel={() => setFormatDialogOpen(false)}
      />

      {/* 操作按钮 */}
      {onNext && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <button
            onClick={onPrev}
            className="flex items-center px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            上一步：分项报价
          </button>
          <button
            onClick={handleNextClick}
            disabled={nextDisabled || matchingResources}
            className="flex items-center px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {matchingResources ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                正在匹配资源...
              </>
            ) : (
              <>
                下一步：文件生成
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </>
            )}
          </button>
        </div>
      )}
      
      {/* 生成目录设置弹框 */}
      <OutlineGenerateSettingsDialog
        show={showGenSettings}
        onClose={() => setShowGenSettings(false)}
        onSave={handleSaveGenSettings}
        outlineRequirements={outlineRequirements}
        initialSettings={genSettings}
      />

      {/* Token 欠费弹窗 */}
      <TokenAlertDialog
        isOpen={tokenAlert.show}
        type={tokenAlert.type}
        title={tokenAlert.title}
        message={tokenAlert.message}
        canContinue={tokenAlert.canContinue}
        onContinue={handleContinueGenerate}
        onRecharge={() => {
          setTokenAlert(prev => ({ ...prev, show: false }));
          navigate('/tokens?openRecharge=true');
        }}
        onClose={() => setTokenAlert(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default OutlineEdit;